/**
 * Single source of truth for page.pageName / page.pageNameNoVehicle.
 *
 * Ford and Lincoln both specify (DTM Data Layer Mapping sheet):
 *   page.pageName          -> Adobe pagename
 *   page.pageNameNoVehicle -> Adobe eVar11 / prop11 ("Pagename No Vehicle")
 *
 * The rule, from 470 spec rows across both workbooks:
 *   pageNameNoVehicle is the base page name, INCLUDING form steps
 *   (:contact us, :thank you, :vrfq, :test drive, ...).
 *   pageName is that base plus the vehicle nameplate as the tail segment.
 *   The only segment ever removed is a nameplate.
 *
 * An event that declares no page name must not call this function at all --
 * it inherits both fields from window.pageLoadDigitaldata via the merge in
 * src/utils/forddirectnative/utils.js.
 *
 * pageNameNoVehicle must never be empty (or whitespace-only): Ford's Launch
 * data element substitutes the literal string "na" for an unset value using
 * an `"" != value` guard that a whitespace-only string sails straight through,
 * and its Ford Advantage custom code calls .indexOf() on the value unguarded.
 *
 * The nameplate-strip is idempotent but never removes a form step
 * (:contact us, :thank you, :vrfq, :vrmi, :test drive, :send to mobile,
 * :redeem offer) even if a candidate nameplate happens to match one --
 * pageNameNoVehicle must retain every form step per the rule above.
 *
 * See docs/forddirectnative-page-names.md.
 *
 * @param {object} [args]
 * @param {string} args.pageNameNoVehicle - Base page name, may already be
 *   `dc:`-prefixed and may already end with the nameplate.
 * @param {string} [args.nameplate] - Vehicle nameplate to append as the tail
 *   segment of pageName. Ignored when blank or not a string -- in
 *   particular, `null` is a deliberate, explicit "suppress" signal (see
 *   pagealias/lincoln.json's SITEBUILDER_2026_LINCOLN_NAUTILUS_SUV_1 /
 *   *_2025_LINCOLN_AVIATOR* / *_2025_LINCOLN_NAVIGATOR* entries), not
 *   merely an absent value; callers that need to distinguish "no nameplate
 *   configured" from "no nameplate wanted" must do so before calling this
 *   function, since both collapse to the same no-append behavior here.
 * @returns {{ pageName: string, pageNameNoVehicle: string }} Both `dc:`-prefixed.
 */
const DC = 'dc:';

// Mirrors test/pre-build/mappings/forddirectnative/pageNames.golden.test.js's FORM_STEPS.
const FORM_STEPS = [
	'contact us',
	'thank you',
	'vrfq',
	'vrmi',
	'test drive',
	'send to mobile',
	'redeem offer'
];

const prefix = (value) => (value.startsWith(DC) ? value : `${DC}${value}`);

const isFormStep = (value) => FORM_STEPS.includes(value.toLowerCase());

export default ({ pageNameNoVehicle, nameplate } = {}) => {
	if (typeof pageNameNoVehicle !== 'string' || !pageNameNoVehicle.trim()) {
		throw new TypeError(
			'derivePageNames: pageNameNoVehicle is required and must be a non-empty string'
		);
	}

	let base = pageNameNoVehicle;
	const tail =
		typeof nameplate === 'string' && nameplate ? nameplate : undefined;

	// The base already fully IS the nameplate, with no leading page segment.
	// There is nothing to strip, and re-appending would just duplicate it.
	const isExactNameplateMatch =
		tail !== undefined && base.toLowerCase() === tail.toLowerCase();

	// Some pagealias configs bake the static nameplate into the stored string.
	// Strip it so the base is genuinely nameplate-free, then re-append below.
	// This keeps the function idempotent regardless of which shape it is given.
	// Never strip a candidate that is itself a form step -- see the JSDoc above.
	if (
		tail &&
		!isExactNameplateMatch &&
		!isFormStep(tail) &&
		base.toLowerCase().endsWith(`:${tail.toLowerCase()}`)
	) {
		base = base.slice(0, -(tail.length + 1));
	}

	const appendTail = tail && !isExactNameplateMatch;

	return {
		pageName: prefix(appendTail ? `${base}:${tail}` : base),
		pageNameNoVehicle: prefix(base)
	};
};
